import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Stack, Text, Alert, Title, Container, Group } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Something went wrong"
          color="red"
          variant="light"
        >
          <Stack gap="sm">
            <Text size="sm">{this.state.error?.message || 'An unexpected error occurred'}</Text>
            {this.props.onReset && (
              <Button
                size="xs"
                variant="light"
                onClick={async () => {
                  try {
                    // Call reset function first (may be sync or async)
                    await this.props.onReset?.();
                    // Clear error state to allow component to re-render
                    // The reset function should have cleared the query error state
                    this.setState({ hasError: false, error: null });
                  } catch (err) {
                    // If reset throws synchronously or asynchronously, keep error state and log
                    console.error('Error resetting boundary:', err);
                    // Error state remains, user can try again
                  }
                }}
              >
                Try Again
              </Button>
            )}
          </Stack>
        </Alert>
      );
    }

    return this.props.children;
  }
}

export function RouteErrorBoundary() {
  const error = useRouteError();

  return (
    <Container className="root-error-boundary" py={80}>
      <Title className="title" ta="center" order={1}>
        Something went wrong!
      </Title>
      <Text ta="center" c="dimmed" size="lg" mt="md" className="description">
        {isRouteErrorResponse(error)
          ? `${error.status} ${error.statusText}`
          : error instanceof Error
            ? error.message
            : 'An unexpected error occurred.'}
      </Text>
      <Group justify="center" mt="xl">
        <Button component={Link} to="/" size="md" variant="outline">
          Get back to home page
        </Button>
      </Group>
    </Container>
  );
}

export function NotFoundError() {
  return (
    <Container className="not-found-error" py={80}>
      <Title className="title" ta="center" order={1}>
        404
      </Title>
      <Text ta="center" c="dimmed" size="lg" mt="md" className="description">
        Page you are trying to open does not exist. You may have mistyped the address, or the page
        has been moved to another URL.
      </Text>
      <Group justify="center" mt="xl">
        <Button component={Link} to="/" size="md" variant="outline">
          Get back to home page
        </Button>
      </Group>
    </Container>
  );
}
